// OPTIONS SCANNER · settings (per user, `ic_settings`). No symbol list: every NFO / MCX option
// underlying is swept; FOCUS only chooses which chains stream continuously. Filters are blank unless set.
// Strategy Type picks what the scanner searches for: Iron Condor (unchanged) or Box Spread (box_* settings).
import React, { useEffect, useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import optionsApi, { optionsError } from "@/lib/optionsApi";
import { CONTROL, FilterField } from "@/components/options/ScannerTable";
import ResetDefaultsButton from "@/components/ResetDefaultsButton";

// kept as the user set them: what to scan for, the personal focus list, and start/stop
export const KEEP_ON_DEFAULTS = new Set(["strategy", "focus", "running", "version"]);

/** The draft that turns `settings` into the recommended defaults (only fields that differ). */
export function defaultsDraft(settings, defaults) {
  const out = {};
  for (const [k, v] of Object.entries(defaults || {})) {
    if (KEEP_ON_DEFAULTS.has(k) || !(k in (settings || {}))) continue;
    if (JSON.stringify(settings[k]) !== JSON.stringify(v)) out[k] = v;
  }
  return out;
}

const NUM_FIELDS = [
  ["lots", "Lots (all legs)", "Identical quantity on all four legs"],
  ["min_wing_strikes", "Min wing (strikes)", "Wing at least this many strikes beyond its short"],
  ["max_wing_strikes", "Max wing (strikes)", "Wing at most this many strikes beyond its short"],
  ["min_distance_pct", "Min distance %", "Shorts at least this % away from the underlying (blank = no minimum)"],
  ["min_net_credit", "Min net credit ₹", "Per condor, all lots (blank = no filter)"],
  ["max_loss", "Max loss ₹", "Per condor, all lots (blank = no filter)"],
  ["min_return_on_risk_pct", "Min return on risk %", "Max profit / max loss (blank = no filter)"],
  ["min_credit_to_width_pct", "Min credit / width %", "Net credit / widest wing (blank = no filter)"],
  ["min_oi", "Min OI / leg", "Open interest on every leg"],
  ["min_volume", "Min volume / leg", "Today's traded volume on every leg"],
  ["max_leg_spread_pct", "Max spread % / leg", "(offer − bid) / offer"],
  ["max_quote_age_s", "Max quote age s", "Older depth = leg refused; sweep rows older than this show STALE"],
  ["token_budget", "Token budget", "WebSocket tokens Iron Condor may use (the Options scanner keeps priority)"],
  ["sweep_batch_wait_s", "Sweep wait s", "How long a sweep batch waits for first ticks"],
  ["sweep_max_expiry_days", "Sweep max DTE", "ALL_WITHIN: every expiry up to this many days"],
  ["keep_per_chain", "Rows / chain", "Best rows kept per underlying / expiry"],
  ["margin_top_n", "Margin top N", "Broker basket margin fetched automatically for the top N rows"],
];
export const RANGE_FIELDS = [
  ["strikes_below_atm", "Strikes below ATM", "Listed strikes under ATM searched (ATM itself is always included)"],
  ["strikes_above_atm", "Strikes above ATM", "Listed strikes over ATM searched (asymmetric allowed)"],
  ["max_range_strikes", "Max strikes / chain", "Operational cap (2 WebSocket tokens per strike). A larger request is scanned nearest ATM and reported PARTIAL"],
];
const SHARED = new Set(["lots", "min_oi", "min_volume", "token_budget", "sweep_batch_wait_s", "sweep_max_expiry_days", "margin_top_n"]);
export const BOX_NUM_FIELDS = [
  ["box_min_width", "Min box width", "K2 − K1 in points (blank = any). Not a breakeven"],
  ["box_max_width", "Max box width", "K2 − K1 in points (blank = any)"],
  ["box_min_profit", "Min profit", "Minimum estimated expiry P&L in the chosen unit (blank = no filter, 0 = positive only)"],
  ["box_max_margin", "Max margin ₹", "Broker SPAN + exposure (blank = no filter; unknown margin shows as pending)"],
  ["box_max_capital", "Max capital ₹", "Broker basket total (blank = no filter; unknown shows as pending)"],
  ["box_max_leg_spread_pct", "Max spread % / leg", "(offer − bid) / offer"],
  ["box_max_quote_age_s", "Max quote age s", "Each leg's newest depth packet"],
  ["box_max_leg_skew_s", "Max leg skew s", "Newest minus oldest of the four legs' packets: quotes must be from the same moment"],
  ["box_slippage_ticks", "Slippage ticks / leg", "Deducted in the net figure"],
  ["box_settlement_stt_pct", "Settlement STT %", "NFO index: STT on in-the-money long legs at expiry"],
  ["box_keep_per_chain", "Rows / chain", "Kept per underlying / expiry (dropped rows are counted)"],
];
const BAND_FIELDS = [
  ["box_band_lower", "Band below", "Points or % below the underlying price"],
  ["box_band_upper", "Band above", "Points or % above the underlying price (asymmetric allowed)"],
];
const BOX_NULLABLE = new Set(["box_band_lower", "box_band_upper", "box_min_width", "box_max_width", "box_min_profit", "box_max_margin", "box_max_capital"]);
const NULLABLE = new Set(["min_distance_pct", "min_net_credit", "max_loss", "min_return_on_risk_pct", "min_credit_to_width_pct", ...BOX_NULLABLE]);
const ALL_NUM = [...NUM_FIELDS, ...BOX_NUM_FIELDS, ...RANGE_FIELDS, ...BAND_FIELDS];

// The five settings shown by default (same keys, validation and saved values; everything else is Advanced).
export const MAIN_IC = [
  ["min_distance_pct", "Protection Range (Both Side) %", "Short strikes at least this % from the underlying, both sides (blank = no minimum)"],
  ["min_net_credit", "Minimum Profit ₹", "Minimum net credit per condor, all lots (blank = no filter)"],
  ["max_loss", "Maximum Loss ₹", "Maximum theoretical loss per condor, all lots (blank = no filter)"],
  ["strikes_above_atm", "Max Strikes Above ATM", "Listed strikes over ATM searched (ATM itself is always included)"],
  ["strikes_below_atm", "Max Strikes Below ATM", "Listed strikes under ATM searched"],
  ["min_oi", "Minimum OI", "Open interest on every leg"],
];
const BASIS_UNIT = { POSITION: "₹ whole position", LOT: "₹ per lot", UNIT: "₹ per unit", ROC_PCT: "% return on capital" };
export const mainFields = (box, basis) => (box
  ? [["box_min_profit", `Minimum Profit (${BASIS_UNIT[basis] || "₹"})`, "Minimum estimated expiry P&L in the unit chosen under Advanced (blank = no filter, 0 = positive only)"],
    MAIN_IC[3], MAIN_IC[4], MAIN_IC[5]]
  : MAIN_IC);
const BOX_SELECTS = [
  ["box_direction", "Direction", [["BOTH", "Long + Short"], ["LONG", "Long box"], ["SHORT", "Short box"]]],
  ["box_lower_region", "Lower strike K1", [["ALL", "All"], ["ITM", "ITM"], ["ATM", "ATM"], ["OTM", "OTM"]]],
  ["box_upper_region", "Upper strike K2", [["ALL", "All"], ["ITM", "ITM"], ["ATM", "ATM"], ["OTM", "OTM"]]],
  ["box_band_mode", "Band unit", [["ATM_STRIKES", "Strikes around ATM (search range)"], ["POINTS", "Points"], ["PCT", "% of underlying"]]],
  ["box_profit_measure", "Profit measure", [["NET", "Net (after est. costs)"], ["GROSS", "Gross"]]],
  ["box_profit_basis", "Min profit unit", [["POSITION", "₹ whole position"], ["LOT", "₹ per lot"], ["UNIT", "₹ per underlying unit"], ["ROC_PCT", "% return on capital"]]],
  ["box_sort", "Rank by", [["net_pnl", "Net min expiry P&L"], ["roc", "Return on capital"], ["gross_edge", "Gross edge % of width"],
    ["capital", "Lowest capital"], ["liquidity", "Liquidity (depth)"], ["charges", "Lowest charges"]]],
];

export default function IcSettings({ settings, onSaved, onClose }) {
  const [draft, setDraft] = useState({});
  const [error, setError] = useState(null);
  const [unders, setUnders] = useState([]);
  const [pick, setPick] = useState({ exchange: "NFO", underlying: "", expiry: "" });
  useEffect(() => { optionsApi.get("/ic/underlyings").then((r) => setUnders(r.data.underlyings || [])).catch(() => {}); }, []);
  const s = { ...settings, ...draft };
  const box = s.strategy === "BOX";
  const names = useMemo(() => unders.filter((u) => u.exchange === pick.exchange), [unders, pick.exchange]);
  const picked = names.find((u) => u.underlying === pick.underlying);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const main = mainFields(box, s.box_profit_basis);
  const mainKeys = new Set(main.map(([k]) => k));
  const field = ([k, label, title], width = "w-28", prefix = "ic-set") => (
    <FilterField key={k} label={label} title={title}>
      <input className={`${CONTROL} ${width} mono text-right`} value={s[k] ?? ""} placeholder={NULLABLE.has(k) ? "none" : ""}
        onChange={(e) => set(k, e.target.value)} data-testid={`${prefix}-${k}`} />
    </FilterField>);
  const save = async () => {
    const body = {};
    for (const [k, v] of Object.entries(draft)) {
      if (ALL_NUM.some(([f]) => f === k)) {
        if (v === "" || v === null) { if (NULLABLE.has(k)) body[k] = null; else { setError(`${k} needs a value`); return; } }
        else if (!Number.isFinite(Number(v))) { setError(`${k} must be a number`); return; }
        else body[k] = Number(v);
      } else body[k] = v;
    }
    // success: the parent closes the dialog and refreshes; failure: stay open, keep every entered value
    try { const r = await optionsApi.put("/ic/settings", body); setError(null); toast.success("Scanner settings saved"); setDraft({}); onSaved(r.data); }
    catch (e) { setError(optionsError(e)); }
  };
  const addFocus = () => {
    if (!pick.underlying) return;
    const item = { exchange: pick.exchange, underlying: pick.underlying, expiry: pick.expiry || null };
    set("focus", [...(s.focus || []).filter((f) => !(f.exchange === item.exchange && f.underlying === item.underlying && f.expiry === item.expiry)), item]);
  };
  const resetDefaults = async () => {
    try {
      const r = await optionsApi.get("/ic/settings/defaults");
      const d = defaultsDraft(settings, r.data);
      setDraft(d);
      toast.success(Object.keys(d).length ? "Recommended settings loaded. Press Save to keep them." : "Already on the recommended settings.");
    } catch (e) { toast.error(optionsError(e)); }
  };
  // scanner audit B6: an opt-in preset fills the draft like the defaults do; Save keeps it
  const loadPreset = async (name) => {
    try {
      const r = await optionsApi.get("/ic/settings/presets");
      const p = (r.data.presets || []).find((x) => x.name === name);
      if (!p) throw new Error(`preset ${name} not available`);
      setDraft(defaultsDraft(settings, p.settings));
      toast.success(`${p.label} loaded. Press Save to keep it.`);
    } catch (e) { toast.error(optionsError(e)); }
  };
  const toggleEx = (e) => set("exchanges", (s.exchanges || []).includes(e) ? s.exchanges.filter((x) => x !== e) : [...(s.exchanges || []), e]);
  return (
    <div className="space-y-3 text-xs" data-testid="ic-settings">
      <div className="text-[10px] text-[var(--term-muted)]">every NFO and MCX option underlying in today's broker master is scanned — no symbol list</div>
      <div className="flex flex-wrap gap-3 items-end" data-testid="ic-main-settings">
        <FilterField label="Strategy type" title="What the scanner searches for. Switching drops the other strategy's rows and restarts the sweep.">
          <select className={`${CONTROL} font-bold`} value={s.strategy || "IRON_CONDOR"} onChange={(e) => set("strategy", e.target.value)}
            data-testid="ic-strategy"><option value="IRON_CONDOR">Iron Condor</option><option value="BOX">Box Spread</option></select>
        </FilterField>
        {main.map((f) => field(f, "w-28", "ic-main"))}
      </div>
      <details className="border border-[var(--term-border)] rounded-sm" data-testid="ic-advanced">
        <summary className="cursor-pointer px-2 py-1.5 text-[11px] font-bold">Advanced Settings</summary>
        <div className="p-2 space-y-3">
      <div className="flex flex-wrap gap-3 items-end">
        <FilterField label="Sweep exchanges">
          <span className="flex gap-2">{["NFO", "MCX"].map((e) => (
            <label key={e} className="flex items-center gap-1"><input type="checkbox" checked={(s.exchanges || []).includes(e)} onChange={() => toggleEx(e)}
              data-testid={`ic-ex-${e}`} />{e}</label>))}</span>
        </FilterField>
        <FilterField label="Universe sweep">
          <label className="flex items-center gap-1 h-8"><input type="checkbox" checked={!!s.sweep_enabled} onChange={(e) => set("sweep_enabled", e.target.checked)} data-testid="ic-sweep" />on</label>
        </FilterField>
        <FilterField label="Sweep expiries">
          <select className={CONTROL} value={s.sweep_expiry_mode} onChange={(e) => set("sweep_expiry_mode", e.target.value)}>
            <option value="NEAREST">Nearest only</option><option value="ALL_WITHIN">All within max DTE</option></select>
        </FilterField>
        {!box && <FilterField label="Rank by">
          <select className={CONTROL} value={s.sort} onChange={(e) => set("sort", e.target.value)} data-testid="ic-sort">
            <option value="return_on_risk">Return on risk</option><option value="net_credit">Net credit</option>
            <option value="credit_to_width">Credit / width</option><option value="max_loss">Lowest max loss</option>
            <option value="distance">Distance from spot</option></select>
        </FilterField>}
      </div>
      <div className="space-y-1 border-l-2 border-[#00C805] pl-2" data-testid="range-settings">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--term-muted)]">Search range (both strategies)</div>
        <div className="text-[10px] text-[var(--term-muted)]">Counted in exchange-listed strikes (CE and PE both listed). ATM = the listed strike nearest
          the underlying price (index spot, NSE share, or the MCX future of that expiry); exactly midway takes the lower strike.
          The range runs from {s.strikes_below_atm ?? "?"} strikes below ATM to {s.strikes_above_atm ?? "?"} above it, both boundaries and ATM included
          ({(Number(s.strikes_below_atm) || 0) + (Number(s.strikes_above_atm) || 0) + 1} strikes requested per chain).
          {box && s.box_band_mode !== "ATM_STRIKES" ? " Box Spread is set to a points / % band instead (Band unit)." : ""}</div>
        <div className="flex flex-wrap gap-3 items-end">
          {RANGE_FIELDS.filter(([k]) => !mainKeys.has(k)).map(([k, label, title]) => (
            <FilterField key={k} label={label} title={title}>
              <input className={`${CONTROL} w-24 mono text-right`} value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`range-set-${k}`} />
            </FilterField>))}
          {!box && <FilterField label="Short strikes" title="Off: short put below and short call above the underlying (OTM). On: any in-range strike, keeping K2 < K3">
            <label className="flex items-center gap-1 h-8"><input type="checkbox" checked={!!s.ic_allow_itm_shorts}
              onChange={(e) => set("ic_allow_itm_shorts", e.target.checked)} data-testid="ic-allow-itm" />allow ITM</label></FilterField>}
          {!box && <FilterField label="Wing extension" title="On: protective wings may sit up to Max wing strikes beyond the range (subscribed and shown in Coverage)">
            <label className="flex items-center gap-1 h-8"><input type="checkbox" checked={!!s.ic_wing_extension}
              onChange={(e) => set("ic_wing_extension", e.target.checked)} data-testid="ic-wing-ext" />wings beyond range</label></FilterField>}
        </div>
      </div>
      {box && <div className="space-y-2 border-l-2 border-[#2563EB] pl-2" data-testid="box-settings">
        <div className="text-[10px] text-[var(--term-muted)]">Box Spread: LONG = BUY K1 CE · SELL K1 PE · SELL K2 CE · BUY K2 PE; SHORT is the reverse.
          The band is a strike-search range, not loss protection. ITM / ATM / OTM are read for the CALL at each strike.
          NFO stock options (physical delivery) are excluded.</div>
        <div className="flex flex-wrap gap-3 items-end">
          {BOX_SELECTS.map(([k, label, opts]) => (
            <FilterField key={k} label={label}>
              <select className={CONTROL} value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`box-set-${k}`}>
                {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            </FilterField>))}
        </div>
        <div className="flex flex-wrap gap-3">
          {(s.box_band_mode === "ATM_STRIKES" ? [] : BAND_FIELDS).concat(BOX_NUM_FIELDS).filter(([k]) => !mainKeys.has(k)).map(([k, label, title]) => (
            <FilterField key={k} label={label} title={title}>
              <input className={`${CONTROL} w-28 mono text-right`} value={s[k] ?? ""} placeholder={NULLABLE.has(k) ? "none" : ""}
                onChange={(e) => set(k, e.target.value)} data-testid={`box-set-${k}`} />
            </FilterField>))}
        </div>
      </div>}
      <div className="flex flex-wrap gap-3">
        {NUM_FIELDS.filter(([k]) => (!box || SHARED.has(k)) && !mainKeys.has(k)).map(([k, label, title]) => (
          <FilterField key={k} label={label} title={title}>
            <input className={`${CONTROL} w-28 mono text-right`} value={s[k] ?? ""} placeholder={NULLABLE.has(k) ? "none" : ""}
              onChange={(e) => set(k, e.target.value)} data-testid={`ic-set-${k}`} />
          </FilterField>))}
      </div>
      <div className="space-y-1">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--term-muted)]">Focus (streamed live)</div>
        <div className="flex flex-wrap gap-1">{(s.focus || []).map((f) => (
          <span key={`${f.exchange}${f.underlying}${f.expiry}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#2563EB]/15 mono">
            {f.exchange}:{f.underlying}{f.expiry ? ` ${f.expiry}` : " (nearest)"}
            <button onClick={() => set("focus", s.focus.filter((x) => x !== f))} aria-label="Remove"><X className="h-3 w-3" /></button></span>))}
          {!(s.focus || []).length && <span className="text-[var(--term-muted)]">none — every chain comes from the sweep</span>}</div>
        <div className="flex flex-wrap gap-2 items-end">
          <select className={CONTROL} value={pick.exchange} onChange={(e) => setPick({ exchange: e.target.value, underlying: "", expiry: "" })}>
            <option>NFO</option><option>MCX</option></select>
          <input list="ic-underlyings" className={`${CONTROL} w-40`} placeholder="underlying" value={pick.underlying}
            onChange={(e) => setPick({ ...pick, underlying: e.target.value.toUpperCase(), expiry: "" })} data-testid="ic-focus-underlying" />
          <datalist id="ic-underlyings">{names.map((u) => <option key={u.underlying} value={u.underlying} />)}</datalist>
          <select className={CONTROL} value={pick.expiry} onChange={(e) => setPick({ ...pick, expiry: e.target.value })}>
            <option value="">nearest expiry</option>{(picked?.expiries || []).map((x) => <option key={x}>{x}</option>)}</select>
          <button type="button" onClick={addFocus} className="h-8 px-2 rounded-sm border border-[var(--term-border-strong)] inline-flex items-center gap-1" data-testid="ic-focus-add">
            <Plus className="h-3 w-3" />Add</button>
        </div>
      </div>
        </div>
      </details>
      {error && <div className="text-[11px] font-bold text-[#FF3B30]" role="alert" data-testid="ic-settings-error">Not saved: {error}</div>}
      <div className="flex justify-end gap-2">
        <ResetDefaultsButton onReset={resetDefaults} testId="ic-reset-defaults" className="mr-auto"
          title="Load the recommended scanner settings into this form (strategy type and focus list are kept). Nothing is saved until Save." />
        <button className="h-8 px-3 rounded-sm border border-[var(--term-border-strong)]" onClick={() => loadPreset("EXECUTABLE")}
          data-testid="ic-preset-executable"
          title="Quotes at most 10 s old, leg spread at most 3 %, every leg traded today. Proposed values, pending market-hours measurement. Nothing is saved until Save.">Load Executable (LIVE) preset</button>
        <button className="h-8 px-3 rounded-sm border border-[var(--term-border-strong)]" onClick={() => setDraft({})}
          title="Discard unsaved edits">Reset</button>
        <button className="h-8 px-3 rounded-sm font-bold bg-[#2563EB] text-white disabled:opacity-40" disabled={!Object.keys(draft).length}
          onClick={save} data-testid="ic-settings-save">Save</button>
      </div>
    </div>
  );
}
